import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ReferralRelativesChoice } from '@/components/patient/ReferralRelativesChoice';
import { selectedReferralRelative, type RelativeChoice } from '@/lib/referral/referralRelative';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  HospitalReferralAttachments,
  fileLimitText,
  type AttachmentRow,
  type FileAttachmentRow,
} from '@/components/patient/HospitalReferralAttachments';
import { HospitalReferralLpr } from '@/components/patient/HospitalReferralLpr';
import { useReferrerIdentity } from '@/components/patient/useReferrerIdentity';
import { receivesEpikrise } from '@/lib/referral/epikriseCopyReceiver';
import { referrerForRequest, referrerNameIssue, referrerTitleIssue } from '@/lib/referral/referrerIdentity';
import {
  DiagnosisField,
  type SelectedDiagnosis,
} from '@/components/patient/ReferralDiagnosisField';
import {
  ReferralBuiltPreview,
  type ReferralCreatedInfo,
} from '@/components/patient/ReferralBuiltPreview';
import {
  Grid,
  LabeledText,
  RadioGroup,
  ReadOnly,
  ReferralBlockingNotice,
  ReferralFormChrome,
  Section,
  TextArea,
  ReferralSubmitError,
} from '@/components/patient/ReferralFormLayout';
import { Hospital, Plus, Send } from 'lucide-react';
import { patientHenvisningPath, patientTabPath } from '@/lib/patientRecordPaths';
import { SorPicker, type PickedReceiver } from '@/components/patient/ReferralSorPicker';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { useAuth } from '@/lib/auth';
import { useClinic } from '@/hooks/useClinicSettings';
import { useClinicCommunicateConnection } from '@/hooks/useCareCommunications';
import { useDecryptCpr } from '@/hooks/usePatients';
import { usePatientRelations } from '@/hooks/usePatientRelations';
import { useSorBySorId } from '@/hooks/useSorLookup';
import { useLprForloebForPatient, useLprSksCodes } from '@/hooks/useLpr';
import { ApiCallError, callFunction } from '@/lib/api';
import { referralBuildFailureMessage } from '@/lib/referral/referralBuildError';
import { uiDateLocale } from '@/lib/i18n';
import { supabase } from '@/lib/supabase';
import {
  MEDCOM_MAX_TELEPHONE_SUBSCRIBERS,
  readTelephoneSubscribers,
  type MedcomTelephoneCode,
} from '@/lib/patientStamkortFields';
import { formatCprDisplay } from '@/lib/cpr';
import { readClinicMedicalSpecialityCode } from '@/lib/referral/clinicMedicalSpecialityCode';
import { medicalSpecialityLabel } from '@/lib/referral/medicalSpecialityLabel';
import {
  CLINICAL_INFO_MAX_TOTAL,
  DIAGNOSE_CODE_MAX,
  DIAGNOSE_DESCRIPTION_CODE,
  DIAGNOSE_TYPE_CODE,
  PARTY_MAX,
  TEXT_FIELD_MAX,
  WARNING_ANAMNESIS,
  WARNING_ANDET,
  WARNING_DIAGNOSIS,
  clinicalInfoCombinedLength,
  clinicalInfoOverLimit,
  clinicalReasonPayload,
  cprDigits,
  hasClinicalReason,
  MISSING_CLINICAL_REASON_MESSAGE,
  diagnoseTextForReferral,
  firstNonLatin1,
  isLatin1,
  MAX_REFERENCES,
  isLprHospitalChoiceCode,
  isLprIllness,
  medcomRelationCode,
  newEpisodeIdentifier,
  protocolWarningFocus,
  protocolWarnings,
  readPostalAddress,
  receiverIdentifierPair,
  referralLprFields,
  senderContactGaps,
  stamkortLengthViolations,
  isUnder15,
  under15BlocksReferral,
  type IdentifierPairGap,
  type LprHospitalChoiceCode,
  type LprIllness,
  type ProtocolFocus,
  type ReceiverIdentifierParty,
  type ReferralLprReason,
  type SenderContactGap,
  type StamkortField,
  type Xref01EpisodeRequest,
  type Xref01IdentifierCode,
} from '@/lib/referral/xref01Validation';
import {
  checkReferralFile,
  medbinObjectType,
  referralReferencesForRequest,
  type AttachmentDraft,
  type ReferralReference,
  type ReferralReferenceIssue,
} from '@/lib/referral/referralAttachments';
import {
  MAX_ADDITIONAL_DIAGNOSES,
  canAddAdditionalDiagnosis,
} from '@/lib/referral/additionalDiagnoses';
import {
  WARNING_UNDER15_CHOOSE_RELATIVE,
  WARNING_UNDER15_NO_RELATIVE,
  WARNING_UNDER15_RELATIONS_ERROR,
  XREF01_ADDITIONAL_DIAGNOSE_DEFAULT,
  XREF01_ADDITIONAL_DIAGNOSE_DESCRIPTION_CODES,
  XREF01_ADDITIONAL_DIAGNOSE_DESCRIPTION_LABELS_DA,
  XREF01_ADDITIONAL_TYPE_LABEL_DA,
  XREF01_REFER_DIAGNOSE_DEFAULT,
  XREF01_REFER_DIAGNOSE_DESCRIPTION_CODES,
  XREF01_REFER_DIAGNOSE_DESCRIPTION_LABELS_DA,
  XREF01_REFER_TYPE_LABEL_DA,
  additionalDiagnoseDescriptionCodeOrDefault,
  referDiagnoseDescriptionCodeForRequest,
  referDiagnoseDescriptionCodeOrDefault,
  type Xref01AdditionalDiagnoseDescriptionCode,
  type Xref01ReferDiagnoseDescriptionCode,
} from '@/lib/referral/xref01Codes';
import { FORM_ERROR_TYPE, FORM_LABEL_TYPE, TYPE, UI_MUTED } from '@/lib/typography';
import { cn } from '@/utils/cn';
import type { Json } from '@/types/database.generated';

export interface HospitalReferralPatient {
  id: string;
  clinic_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  job_title: string | null;
  address: Json;
  telephone_subscribers: Json | null;
  has_cpr: boolean;
}

type Priority = 'elektiv' | 'akut' | 'saerlige_forhold';
type Stay = 'ambulant' | 'indlaeggelse';
type TransportMode = 'ingen' | 'liggende' | 'siddende';

type AdditionalRow = {
  key: string;
  diagnosis: SelectedDiagnosis | null;
  diagnoseDescriptionCode: Xref01AdditionalDiagnoseDescriptionCode;
};

type RefCheck = { id: string; pass: boolean; detail: string };
type RefPreview = {
  ok: boolean;
  xml: string;
  checks: RefCheck[];
  id?: string;
  outboxId?: string;
};

type DiagnosisBody = {
  diagnoseDescriptionCode: string;
  diagnoseCode: string;
  diagnoseTypeCode: string;
  diagnoseText: string;
};

interface Xref01Request extends Record<string, unknown> {
  clinicId: string;
  patientId: string;
  kind: 'XREF01';
  acknowledgementCode: 'pluspositivkvitt';
  sender: {
    ean: string;
    identifier: string;
    identifierCode: string;
    organisationName: string;
    streetName?: string;
    districtName?: string;
    postCodeIdentifier?: string;
    telephoneSubscriberIdentifier?: string;
    medicalSpecialityCode: string;
    referrer: { personName: string; personTitle: string };
  };
  receiver: {
    ean: string;
    identifier: string;
    identifierCode: string;
    organisationName?: string;
    streetName?: string;
    districtName?: string;
    postCodeIdentifier?: string;
  };
  answerCCReceiver?: Xref01Request['receiver'];
  patient: {
    civilRegistrationNumber: string;
    personSurnameName: string;
    personGivenName?: string;
    personTitle?: string;
    streetName?: string;
    districtName?: string;
    postCodeIdentifier?: string;
    emailIdentifier?: string;
    telephoneSubscribers?: {
      telephoneSubscriberIdentifier: string;
      telephoneSubscriberCode: string;
    }[];
  };
  relative?: {
    relationCode: string;
    personIdentifier?: string;
    personSurnameName?: string;
    personGivenName?: string;
  };
  episodesOfCare: Xref01EpisodeRequest[];
  relevantClinicalInformation: {
    anamnesis: string[];
    cave?: string[];
    examination?: string[];
    actualMedicine?: string[];
  };
  additionalInformation: {
    priority: string;
    referralStatus: string;
    transport: string;
    supplementary?: string[];
    clinicalReason?: string[];
    hospitalChoice?: LprHospitalChoiceCode;
  };
  references?: ReferralReference[];
  companions?: { exchangeId: string; fileName: string }[];
  referral: {
    refer: DiagnosisBody;
    additionalDiagnoses?: DiagnosisBody[];
  };
}

const STAMKORT_LABEL: Record<StamkortField, string> = {
  surname: 'Efternavn',
  givenName: 'Fornavn',
  jobTitle: 'Stilling',
  street: 'Adresse',
  postCode: 'Postnr',
  district: 'By',
  email: 'E-mail',
  telephone: 'Telefonnummer',
};

const PHONE_ROWS: { code: MedcomTelephoneCode; label: string }[] = [
  { code: 'fastnet', label: 'Fastnet' },
  { code: 'mobil', label: 'Mobil' },
  { code: 'arbejde', label: 'Arbejde' },
];

function diagnosisBody(
  diagnosis: SelectedDiagnosis,
  diagnoseDescriptionCode: string = DIAGNOSE_DESCRIPTION_CODE,
): DiagnosisBody {
  return {
    diagnoseCode: diagnosis.code,
    diagnoseText: diagnoseTextForReferral(diagnosis.textDa),
    diagnoseTypeCode: DIAGNOSE_TYPE_CODE,
    diagnoseDescriptionCode,
  };
}

export function HospitalReferralForm({
  patient,
  revealedCpr,
  onCprRevealed,
  canRevealCpr,
  onCancel,
  onCreated,
}: {
  patient: HospitalReferralPatient;
  revealedCpr: string | null;
  onCprRevealed: (cpr: string) => void;
  canRevealCpr: boolean;
  onCancel?: () => void;
  onCreated?: (created: ReferralCreatedInfo) => void;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clinicQuery = useClinic(patient.clinic_id);
  const connectionQuery = useClinicCommunicateConnection(patient.clinic_id);
  const senderSor = useSorBySorId(connectionQuery.data?.sor_id ?? null);
  const relationsQuery = usePatientRelations(patient.id);
  const decryptPatient = useDecryptCpr();
  const decryptRelative = useDecryptCpr();
  const medicalSpecialityCode = readClinicMedicalSpecialityCode(
    connectionQuery.data?.medical_speciality_code,
  );

  const [episodeId] = useState(newEpisodeIdentifier);
  const [priority, setPriority] = useState<Priority>('elektiv');
  const [stay, setStay] = useState<Stay>('ambulant');
  const [transport, setTransport] = useState<TransportMode>('ingen');
  const [clinicalReason, setClinicalReason] = useState('');
  const [supplementary, setSupplementary] = useState('');
  const [anamnesis, setAnamnesis] = useState('');
  const [medicine, setMedicine] = useState('');
  const [cave, setCave] = useState('');
  const [examination, setExamination] = useState('');
  const [primary, setPrimary] = useState<SelectedDiagnosis | null>(null);
  const [referType, setReferType] = useState<Xref01ReferDiagnoseDescriptionCode>(
    XREF01_REFER_DIAGNOSE_DEFAULT,
  );
  const [additional, setAdditional] = useState<AdditionalRow[]>([]);
  const [receiver, setReceiver] = useState<PickedReceiver | null>(null);
  const [copyReceiver, setCopyReceiver] = useState<PickedReceiver | null>(null);
  const [attachments, setAttachments] = useState<AttachmentRow[]>([]);
  const [forwardLpr, setForwardLpr] = useState(false);
  const [lprPathwayId, setLprPathwayId] = useState<string | null>(null);
  const [lprIllness, setLprIllness] = useState<LprIllness | null>(null);
  const [lprHospitalChoice, setLprHospitalChoice] = useState<LprHospitalChoiceCode | null>(null);
  const pathwaysQuery = useLprForloebForPatient(patient.clinic_id, patient.id);
  const sksQuery = useLprSksCodes();
  const [attempted, setAttempted] = useState(false);
  const [cprFailed, setCprFailed] = useState(false);
  const [relativeCpr, setRelativeCpr] = useState<string | null>(null);
  const [relativeChoice, setRelativeChoice] = useState<RelativeChoice>(null);
  const [pending, setPending] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [preview, setPreview] = useState<RefPreview | null>(null);

  const diagnosisRef = useRef<HTMLInputElement>(null);
  const andetRef = useRef<HTMLTextAreaElement>(null);
  const anamnesisRef = useRef<HTMLTextAreaElement>(null);
  const clinicalReasonRef = useRef<HTMLTextAreaElement>(null);
  const onCprRevealedRef = useRef(onCprRevealed);
  onCprRevealedRef.current = onCprRevealed;

  const referrerQuery = useReferrerIdentity(user?.id, patient.clinic_id);

  useEffect(() => {
    if (!patient.has_cpr || revealedCpr || !canRevealCpr) return;
    let cancelled = false;
    void decryptPatient
      .mutateAsync({
        patient_id: patient.id,
        source: 'hospital-referral',
        purpose: 'xref01',
      })
      .then(
        (result) => {
          if (!cancelled) onCprRevealedRef.current(result.cpr);
        },
        () => {
          if (!cancelled) setCprFailed(true);
        },
      );
    return () => {
      cancelled = true;
    };
    // mutateAsync is stable; the mutation object is not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRevealCpr, patient.has_cpr, patient.id, revealedCpr]);

  const address = readPostalAddress(patient.address);
  const subscribers = readTelephoneSubscribers(patient.telephone_subscribers);
  const clinicAddress = readPostalAddress(clinicQuery.data?.address);
  const personRelations = (relationsQuery.data ?? []).flatMap((row) => {
    if (!row.to_patient_id || medcomRelationCode(row.relation_type) === null) return [];
    return [{ relationType: row.relation_type, row }];
  });
  const relativeCprDigits = revealedCpr ? cprDigits(revealedCpr) : null;
  const relativeUnder15 = Boolean(relativeCprDigits && isUnder15(relativeCprDigits, new Date()));
  const chosenRelative = selectedReferralRelative(personRelations, relativeChoice, relativeUnder15);
  const chosenPatientId = chosenRelative?.row.to_patient_id ?? null;
  const relatedIds = [...new Set(personRelations.map((item) => item.row.to_patient_id as string))].sort();

  const relatedQuery = useQuery({
    queryKey: ['hospital-referral-relatives', patient.id, relatedIds],
    enabled: relatedIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('patients')
        .select('id, first_name, last_name')
        .in('id', relatedIds);
      if (error) throw error;
      return data ?? [];
    },
  });

  useEffect(() => {
    if (!chosenPatientId || !canRevealCpr) {
      setRelativeCpr(null);
      return;
    }
    let cancelled = false;
    setRelativeCpr(null);
    void decryptRelative
      .mutateAsync({
        patient_id: chosenPatientId,
        source: 'hospital-referral-relative',
        purpose: 'xref01',
      })
      .then(
        (result) => {
          if (!cancelled) setRelativeCpr(result.cpr);
        },
        () => {
          if (!cancelled) setRelativeCpr(null);
        },
      );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRevealCpr, chosenPatientId]);

  const emptyLabel = t('patients.hospital_referral.empty', 'Ikke udfyldt');
  const patientCprDigits = revealedCpr ? cprDigits(revealedCpr) : null;
  const today = new Date();
  const childWithoutRelative = Boolean(
    patientCprDigits &&
      relationsQuery.isSuccess &&
      under15BlocksReferral(patientCprDigits, personRelations.map((item) => item.relationType), today),
  );
  const clinicalLength = clinicalInfoCombinedLength(anamnesis, medicine, cave, examination);
  const selectedAdditional = additional.filter(
    (row): row is AdditionalRow & { diagnosis: SelectedDiagnosis } => row.diagnosis !== null,
  );
  const pathways = pathwaysQuery.data ?? [];
  const selectedPathway = pathways.find((row) => row.id === lprPathwayId) ?? null;
  const fritvalgCodes = (sksQuery.data ?? []).filter(
    (row) => row.list === 'admin.fritvalg' && isLprHospitalChoiceCode(row.code),
  );

  function tooLong(field: string, length: number, max: number): string {
    return t(
      'patients.hospital_referral.too_long',
      '{{field}} er {{length}} tegn. Grænsen er {{max}}. Værdien afkortes ikke.',
      { field, length, max },
    );
  }

  function latin1Message(field: string): string {
    return t(
      'patients.hospital_referral.latin1',
      '{{field}} indeholder et tegn, der ikke kan skrives i ISO-8859-1.',
      { field },
    );
  }

  function attachmentIssueMessage(issue: ReferralReferenceIssue): string {
    const number = 'index' in issue ? issue.index + 1 : 0;
    switch (issue.code) {
      case 'incomplete':
        if (issue.source === 'file') {
          return t(
            'patients.hospital_referral.attachment_file_incomplete',
            'Bilag {{number}}: både beskrivelse og fil skal udfyldes.',
            { number },
          );
        }
        return t(
          'patients.hospital_referral.attachment_incomplete',
          'Bilag {{number}}: både beskrivelse og webadresse skal udfyldes.',
          { number },
        );
      case 'description_long':
        return tooLong(
          t('patients.hospital_referral.attachment_description', 'Beskrivelse'),
          issue.length,
          issue.max,
        );
      case 'url_long':
        return tooLong(
          t('patients.hospital_referral.attachment_url', 'Webadresse'),
          issue.length,
          issue.max,
        );
      case 'latin1':
        return latin1Message(t('patients.hospital_referral.attachments_section', 'Bilag'));
      case 'too_many':
        return t('patients.hospital_referral.attachment_too_many', 'Der kan højst være 10 bilag.');
      case 'unsupported-type':
      case 'file-too-large':
      case 'too-many-files':
      case 'total-too-large':
        return fileLimitText(t, issue.code);
      case 'not_ready':
        return t(
          'patients.hospital_referral.attachment_file_not_ready',
          'Bilag {{number}}: filen er ikke lagt på bilaget.',
          { number },
        );
      case 'zip_type_missing':
        return t(
          'patients.hospital_referral.attachment_zip_type_missing',
          'Bilag {{number}}: vælg, hvad ZIP-filen indeholder.',
          { number },
        );
      default: {
        const unexpected: never = issue;
        return String(unexpected);
      }
    }
  }

  function lprReasonMessage(reason: ReferralLprReason): string {
    switch (reason) {
      case 'missing_illness':
        return t(
          'patients.hospital_referral.lpr_illness_required',
          'Vælg om diagnosen er en ny sygdom eller den samme sygdom.',
        );
      case 'missing_object':
        return t(
          'patients.hospital_referral.lpr_pathway_required',
          'Vælg det LPR-forløb, henvisningen skal følge.',
        );
      case 'bad_identifier':
        return t(
          'patients.hospital_referral.lpr_identifier_invalid',
          'LPR-forløbets id kan ikke bruges i henvisningen.',
        );
      default: {
        const unexpected: never = reason;
        return unexpected;
      }
    }
  }

  function assess(): {
    blocking: string[];
    required: string[];
    pending: string[];
    warnings: string[];
    focus: ProtocolFocus | 'clinical_reason' | null;
  } {
    const blocking: string[] = [];
    const required: string[] = [];
    const pendingMessages: string[] = [];
    const warnings: string[] = [];

    if (medicalSpecialityCode === null) {
      blocking.push(
        t(
          'patients.hospital_referral.speciality_block',
          'Klinikkens specialekode er ikke sat op. Henvisningen kan ikke dannes, før specialekoden er konfigureret.',
        ),
      );
    }

    for (const violation of stamkortLengthViolations({
      surname: patient.last_name,
      givenName: patient.first_name,
      jobTitle: patient.job_title ?? '',
      street: address.street,
      postCode: address.postCode,
      district: address.district,
      email: patient.email ?? '',
      telephones: subscribers.map((subscriber) => subscriber.identifier),
    })) {
      blocking.push(tooLong(t(`patients.hospital_referral.stamkort_${violation.field}`, STAMKORT_LABEL[violation.field]), violation.length, violation.max));
    }
    if (subscribers.length > MEDCOM_MAX_TELEPHONE_SUBSCRIBERS) {
      blocking.push(
        t(
          'patients.hospital_referral.too_many_phones',
          'Stamkortet har mere end 3 telefonnumre. MedCom tillader højst 3.',
        ),
      );
    }

    if (!patient.has_cpr) {
      blocking.push(
        t('patients.hospital_referral.cpr_missing', 'Patienten har ikke et CPR-nummer. Henvisningen kan ikke dannes.'),
      );
    } else if (!canRevealCpr) {
      blocking.push(
        t('patients.hospital_referral.cpr_unavailable', 'CPR-nummeret kan ikke vises. Henvisningen kan ikke dannes.'),
      );
    } else if (cprFailed) {
      blocking.push(
        t('patients.hospital_referral.cpr_failed', 'CPR-nummeret kunne ikke læses. Henvisningen kan ikke dannes.'),
      );
    } else if (!revealedCpr) {
      pendingMessages.push(t('patients.hospital_referral.cpr_loading', 'CPR-nummeret indlæses stadig.'));
    } else if (!patientCprDigits) {
      blocking.push(
        t('patients.hospital_referral.cpr_format', 'CPR-nummeret har ikke 10 cifre. Henvisningen kan ikke dannes.'),
      );
    }

    if (patientCprDigits && relationsQuery.isLoading) {
      pendingMessages.push(
        t('patients.hospital_referral.relatives_loading', 'Relationerne indlæses stadig.'),
      );
    }
    if (patientCprDigits && relationsQuery.isError && isChildAge(patientCprDigits, today)) {
      blocking.push(
        t(
          'patients.hospital_referral.relatives_error_under15',
          WARNING_UNDER15_RELATIONS_ERROR,
        ),
      );
    }
    if (childWithoutRelative) {
      blocking.push(
        t(
          'patients.hospital_referral.relatives_under15',
          WARNING_UNDER15_NO_RELATIVE,
        ),
      );
    }
    if (chosenRelative && relatedQuery.isLoading) {
      pendingMessages.push(
        t('patients.hospital_referral.relative_loading', 'Pårørendes navn indlæses stadig.'),
      );
    }
    if (chosenRelative && relatedQuery.isError) {
      blocking.push(
        t(
          'patients.hospital_referral.relative_failed',
          'Pårørendes navn kunne ikke hentes fra stamkortet. Henvisningen kan ikke dannes.',
        ),
      );
    }
    if (chosenRelative && relatedQuery.isSuccess) {
      const names = relatedQuery.data?.find((row) => row.id === chosenRelative.row.to_patient_id);
      if (!names?.last_name) {
        blocking.push(
          t(
            'patients.hospital_referral.relative_failed',
            'Pårørendes navn kunne ikke hentes fra stamkortet. Henvisningen kan ikke dannes.',
          ),
        );
      }
    }

    if (!receiver) {
      required.push(t('patients.hospital_referral.receiver_missing', 'Vælg en modtager.'));
    } else {
      blocking.push(
        ...partyProblems(
          t,
          tooLong,
          t('patients.hospital_referral.receiver_section', 'Modtager'),
          receiver,
        ),
      );
    }
    if (copyReceiver) {
      blocking.push(
        ...partyProblems(
          t,
          tooLong,
          t('patients.hospital_referral.copy_party', 'Kopimodtager'),
          copyReceiver,
        ),
      );
    }

    if (clinicalReason.trim().length > TEXT_FIELD_MAX) {
      blocking.push(
        tooLong(
          t('patients.hospital_referral.clinical_reason', 'Ønsket undersøgelse/behandling/problemstilling'),
          clinicalReason.trim().length,
          TEXT_FIELD_MAX,
        ),
      );
    }
    if (!hasClinicalReason(clinicalReason)) {
      required.push(
        t('patients.hospital_referral.clinical_reason_required', MISSING_CLINICAL_REASON_MESSAGE),
      );
    }
    if (supplementary.length > TEXT_FIELD_MAX) {
      blocking.push(tooLong(t('patients.hospital_referral.andet', 'Andet'), supplementary.length, TEXT_FIELD_MAX));
    }
    if (clinicalInfoOverLimit(anamnesis, medicine, cave, examination)) {
      blocking.push(
        t(
          'patients.hospital_referral.clinical_over',
          'Anamnese, medicin, CAVE og øvrige undersøgelser er tilsammen på {{count}} tegn. Grænsen er {{max}}.',
          {
            count: clinicalLength,
            max: CLINICAL_INFO_MAX_TOTAL.toLocaleString(uiDateLocale()),
          },
        ),
      );
    }
    if (selectedAdditional.length > MAX_ADDITIONAL_DIAGNOSES) {
      blocking.push(
        t('patients.hospital_referral.too_many_diagnoses', 'Der kan højst være fem tillægsdiagnoser.'),
      );
    }
    for (const diagnosis of [primary, ...selectedAdditional.map((row) => row.diagnosis)]) {
      if (diagnosis && diagnosis.code.length > DIAGNOSE_CODE_MAX) {
        blocking.push(tooLong(t('patients.hospital_referral.diagnosis_code', 'Diagnosekode'), diagnosis.code.length, DIAGNOSE_CODE_MAX));
      }
    }

    const protocol = protocolWarnings({
      hasPrimaryDiagnosis: primary !== null,
      priority,
      supplementary,
      anamnesis,
    });
    for (const warning of protocol) {
      if (warning === WARNING_DIAGNOSIS) {
        required.push(t('patients.hospital_referral.diagnosis_required', WARNING_DIAGNOSIS));
      } else if (warning === WARNING_ANDET) {
        required.push(t('patients.hospital_referral.andet_required', WARNING_ANDET));
      } else if (warning === WARNING_ANAMNESIS) {
        required.push(t('patients.hospital_referral.anamnesis_required', WARNING_ANAMNESIS));
      } else {
        const unexpected: never = warning;
        required.push(unexpected);
      }
    }

    const textFields: { field: string; value: string }[] = [
      { field: t('patients.hospital_referral.clinical_reason', 'Ønsket undersøgelse/behandling/problemstilling'), value: clinicalReason },
      { field: t('patients.hospital_referral.andet', 'Andet'), value: supplementary },
      { field: t('patients.hospital_referral.anamnesis', 'Anamnese'), value: anamnesis },
      { field: t('patients.hospital_referral.medicine', 'Medicin'), value: medicine },
      { field: t('patients.hospital_referral.cave', 'CAVE'), value: cave },
      { field: t('patients.hospital_referral.examination', 'Øvrige undersøgelser'), value: examination },
      { field: STAMKORT_LABEL.surname, value: patient.last_name },
      { field: STAMKORT_LABEL.givenName, value: patient.first_name },
      { field: STAMKORT_LABEL.jobTitle, value: patient.job_title ?? '' },
      { field: STAMKORT_LABEL.street, value: address.street },
      { field: STAMKORT_LABEL.postCode, value: address.postCode },
      { field: STAMKORT_LABEL.district, value: address.district },
      { field: STAMKORT_LABEL.email, value: patient.email ?? '' },
      ...subscribers.map((subscriber) => ({ field: subscriber.code, value: subscriber.identifier })),
    ];
    if (receiver) {
      textFields.push({ field: t('patients.hospital_referral.identifier', 'Identifikator'), value: receiver.identifier });
    }
    for (const field of textFields) {
      if (!isLatin1(field.value)) blocking.push(latin1Message(field.field));
    }

    const attachmentResult = referralReferencesForRequest(attachmentDrafts(attachments), {
      requireChecked: false,
    });
    if (!attachmentResult.ok) {
      for (const issue of attachmentResult.issues) {
        blocking.push(attachmentIssueMessage(issue));
      }
    }

    if (forwardLpr && pathwaysQuery.isLoading) {
      pendingMessages.push(t('patients.hospital_referral.lpr_loading', 'LPR-forløb indlæses stadig.'));
    } else if (forwardLpr && pathwaysQuery.isError) {
      blocking.push(
        t(
          'patients.hospital_referral.lpr_error',
          'LPR-forløb kunne ikke hentes. Viderehenvisning kan ikke vælges.',
        ),
      );
    } else if (forwardLpr && pathways.length === 0) {
      blocking.push(
        t(
          'patients.hospital_referral.lpr_none',
          'Patienten har ikke et LPR-forløb. Viderehenvisning i et eksisterende forløb kan ikke vælges.',
        ),
      );
    } else if (forwardLpr) {
      const lpr = referralLprFields({
        referringIdentifier: episodeId,
        forward: true,
        objectId: selectedPathway?.object_id ?? null,
        illness: lprIllness,
        hospitalChoice: lprHospitalChoice,
      });
      if (!lpr.ok) blocking.push(lprReasonMessage(lpr.reason));
    }

    blocking.push(...senderProblems(pendingMessages));
    warnings.push(...senderContactWarningMessages());

    const focus: ProtocolFocus | 'clinical_reason' | null =
      protocolWarningFocus(protocol) ??
      (!hasClinicalReason(clinicalReason) ? 'clinical_reason' : null);
    return {
      blocking,
      required,
      pending: pendingMessages,
      warnings,
      focus,
    };
  }

  function senderProblems(pendingMessages: string[]): string[] {
    const problems: string[] = [];
    if (clinicQuery.isLoading || connectionQuery.isLoading) {
      pendingMessages.push(t('patients.hospital_referral.sender_loading', 'Afsender indlæses stadig.'));
      return problems;
    }
    const clinic = clinicQuery.data;
    const connection = connectionQuery.data;
    if (!clinic || !connection || connection.status !== 'active') {
      problems.push(t('patients.hospital_referral.sender_missing', 'Afsenderen er ikke tilgængelig.'));
      return problems;
    }
    const ean = connection.ean ?? '';
    if (ean.length === 0) {
      problems.push(t('patients.hospital_referral.sender_ean_missing', 'Afsender mangler EAN.'));
    } else if (ean.length > PARTY_MAX.ean) {
      problems.push(tooLong('EAN', ean.length, PARTY_MAX.ean));
    }
    if (!connection.sor_id) {
      problems.push(t('patients.hospital_referral.sender_sor_missing', 'Klinikken har ikke et SOR-id.'));
    } else if (senderSor.isLoading) {
      pendingMessages.push(t('patients.hospital_referral.sender_sor_loading', 'Lokationsnummer indlæses stadig.'));
    } else if (senderSor.error || !senderSor.data) {
      problems.push(t('patients.hospital_referral.sender_sor_failed', 'Klinikken blev ikke fundet i SOR.'));
    } else if (!senderSor.data.identifier || !senderSor.data.identifierCode) {
      problems.push(
        t(
          'patients.hospital_referral.sender_identifier_missing',
          'Lokationsnummeret kan ikke udledes af klinikkens SOR-enhed.',
        ),
      );
    } else if (senderSor.data.identifier.length > PARTY_MAX.identifier) {
      problems.push(tooLong(t('patients.hospital_referral.location_number', 'Lokationsnummer'), senderSor.data.identifier.length, PARTY_MAX.identifier));
    }
    const organisationName = clinic.name ?? '';
    if (organisationName.length === 0) {
      problems.push(t('patients.hospital_referral.sender_name_missing', 'Afsender mangler organisationsnavn.'));
    } else if (organisationName.length > PARTY_MAX.organisationName) {
      problems.push(tooLong(t('patients.hospital_referral.organisation', 'Organisation'), organisationName.length, PARTY_MAX.organisationName));
    }
    pushAddressLengths(problems, tooLong, clinicAddress);
    const phone = clinic.phone ?? '';
    if (phone.length > PARTY_MAX.telephone) {
      problems.push(tooLong(t('patients.hospital_referral.phone', 'Telefon'), phone.length, PARTY_MAX.telephone));
    }
    if (referrerQuery.isLoading) {
      pendingMessages.push(t('patients.hospital_referral.referrer_loading', 'Henvisers navn indlæses stadig.'));
    } else if (referrerQuery.isError) {
      problems.push(t('patients.hospital_referral.referrer_failed', 'Henvisers navn kunne ikke hentes.'));
    } else {
      const referrerName = referrerQuery.data?.name ?? '';
      const nameIssue = referrerNameIssue(referrerName);
      if (nameIssue === 'missing') {
        problems.push(
          t('patients.hospital_referral.referrer_missing', 'Henvisers navn er ikke sat på brugeren.'),
        );
      } else if (nameIssue === 'too-long') {
        problems.push(tooLong(t('patients.hospital_referral.referrer_name', 'Henvisers navn'), referrerName.length, PARTY_MAX.referrerName));
      }
      const referrerTitle = referrerQuery.data?.title ?? '';
      const titleIssue = referrerTitleIssue(referrerTitle);
      if (titleIssue === 'missing') {
        problems.push(
          t(
            'patients.hospital_referral.referrer_title_missing',
            'Henvisers titel er ikke sat. Sæt den under Indstillinger → Brugere → Titel.',
          ),
        );
      } else if (titleIssue === 'too-long') {
        problems.push(
          tooLong(
            t('patients.hospital_referral.referrer_title', 'Henvisers titel'),
            referrerTitle.length,
            PARTY_MAX.referrerTitle,
          ),
        );
      }
    }
    for (const value of [ean, organisationName, clinicAddress.street, clinicAddress.postCode, clinicAddress.district, phone]) {
      if (!isLatin1(value)) {
        problems.push(latin1Message(t('patients.hospital_referral.sender_section', 'Afsender')));
        break;
      }
    }
    return problems;
  }

  function senderContactWarningMessages(): string[] {
    if (clinicQuery.isLoading || !clinicQuery.data) return [];
    const gaps = senderContactGaps(clinicAddress.postCode, clinicQuery.data.phone ?? '');
    if (gaps.length === 0) return [];
    return [senderContactWarningText(t, gaps)];
  }

  const assessment = assess();
  const alertMessages = [
    ...assessment.blocking,
    ...(attempted ? assessment.required : []),
    ...(attempted ? assessment.pending : []),
  ];

  function fileUploadContext(): {
    senderEan: string;
    senderName: string;
    receiverEan: string;
    receiverName: string;
    cpr: string;
    givenName: string;
    surname: string;
  } | null {
    const clinic = clinicQuery.data;
    const connection = connectionQuery.data;
    const givenName = patient.first_name?.trim() ?? '';
    const surname = patient.last_name?.trim() ?? '';
    const receiverName = receiver?.organisation.name?.trim() ?? '';
    if (!clinic?.name || !connection?.ean || !receiver?.organisation.ean || !receiverName) return null;
    if (!patientCprDigits || !givenName || !surname) return null;
    return {
      senderEan: connection.ean,
      senderName: clinic.name,
      receiverEan: receiver.organisation.ean,
      receiverName,
      cpr: patientCprDigits,
      givenName,
      surname,
    };
  }

  async function uploadFileRow(
    row: FileAttachmentRow,
    all: readonly AttachmentRow[],
  ): Promise<FileAttachmentRow> {
    if (!row.file) {
      return {
        ...row,
        error: t(
          'patients.hospital_referral.attachment_file_missing',
          'Vælg filen igen.',
        ),
      };
    }
    const context = fileUploadContext();
    if (!context) {
      return {
        ...row,
        error: t(
          'patients.hospital_referral.attachment_file_context',
          'Vælg modtager, og udfyld patientens fornavn og efternavn, før filen kan vedlægges.',
        ),
      };
    }
    const others = all.filter(
      (item): item is FileAttachmentRow =>
        item.kind === 'file' && item.key !== row.key && item.fileName.trim().length > 0,
    );
    let bytes: Uint8Array;
    try {
      bytes = new Uint8Array(await row.file.arrayBuffer());
    } catch {
      return {
        ...row,
        error: t('patients.hospital_referral.attachment_file_unreadable', 'Filen kunne ikke læses.'),
      };
    }
    try {
      const result = await callFunction<
        {
          clinicId: string;
          kind: 'XBIN02';
          sender: { ean: string; name: string };
          receiver: { ean: string; name: string };
          patientCpr: string;
          patientGivenName: string;
          patientSurname: string;
          fileName: string;
          fileBase64: string;
          existingFileCount: number;
          existingTotalBytes: number;
          objectType?: string;
        },
        MedbinFileResult
      >('communicate-medbin', {
        clinicId: patient.clinic_id,
        kind: 'XBIN02',
        sender: { ean: context.senderEan, name: context.senderName },
        receiver: { ean: context.receiverEan, name: context.receiverName },
        patientCpr: context.cpr,
        patientGivenName: context.givenName,
        patientSurname: context.surname,
        fileName: row.file.name,
        fileBase64: bytesToBase64(bytes),
        existingFileCount: others.length,
        existingTotalBytes: others.reduce((sum, item) => sum + item.byteLength, 0),
        ...(medbinObjectType(row.file.name, row.zipObjectType)
          ? { objectType: medbinObjectType(row.file.name, row.zipObjectType) }
          : {}),
      });
      const failed = result.checks.find((check) => !check.pass);
      if (!result.ok || failed) {
        return { ...row, error: failed?.detail || result.checks[0]?.detail || gatewayFallback(t) };
      }
      const parentReference = result.parentReference?.trim() ?? '';
      if (
        !result.exchangeId ||
        parentReference.length === 0 ||
        parentReference.includes('-') ||
        parentReference.length > 35 ||
        !result.objectCode ||
        !result.objectExtensionCode ||
        !Number.isInteger(result.originalObjectSize) ||
        result.originalObjectSize < 0
      ) {
        return {
          ...row,
          error: t(
            'patients.hospital_referral.attachment_file_not_ready',
            'Filen er ikke lagt på bilaget.',
          ),
        };
      }
      return {
        ...row,
        error: '',
        fileName: row.file.name,
        byteLength: bytes.byteLength,
        exchangeId: result.exchangeId,
        objectIdentifier: parentReference,
        objectCode: result.objectCode,
        objectExtensionCode: result.objectExtensionCode,
        originalObjectSize: result.originalObjectSize,
      };
    } catch (err) {
      return { ...row, error: gatewayFailureText(err, gatewayFallback(t)) };
    }
  }

  async function uploadOutstandingFiles(rows: readonly AttachmentRow[]): Promise<AttachmentRow[]> {
    const next: AttachmentRow[] = [];
    for (const row of rows) {
    if (row.kind !== 'file' || row.exchangeId || !row.file) {
      next.push(row);
      continue;
    }
      next.push(await uploadFileRow(row, rows));
    }
    return next;
  }

  function failSubmit(message: string) {
    setSubmitError(message);
    toast.error(message, { duration: 10_000 });
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const next = assess();
    setAttempted(true);
    const messages = [...next.blocking, ...next.required, ...next.pending];
    if (messages.length > 0) {
      focusField(next.focus);
      return;
    }
    setPending(true);
    setSubmitError(null);
    const prepared = await uploadOutstandingFiles(attachments);
    setAttachments(prepared);
    const refused = prepared.find(
      (row) => row.kind === 'file' && row.fileName.trim().length > 0 && !row.exchangeId,
    );
    if (refused && refused.kind === 'file') {
      failSubmit(refused.error || gatewayFallback(t));
      setPending(false);
      return;
    }
    const body = buildRequest(prepared);
    if (!body) {
      failSubmit(t('patients.hospital_referral.toast_failed', 'Henvisningen kunne ikke dannes.'));
      setPending(false);
      return;
    }
    try {
      const result = await callFunction<Xref01Request, RefPreview>('communicate-ref', body);
      setPreview(result);
      if (result.ok) {
        toast.success(t('patients.hospital_referral.toast_built', 'Henvisningen er dannet.'));
        onCreated?.({ outboxId: result.outboxId, exchangeId: result.id });
      } else {
        toast.error(
          t(
            'patients.hospital_referral.toast_checks_failed',
            'Henvisningen er dannet, men kontrollen fandt mangler.',
          ),
        );
      }
    } catch (err) {
      failSubmit(
        referralBuildFailureMessage(
          err,
          t('patients.hospital_referral.toast_failed', 'Henvisningen kunne ikke dannes.'),
        ),
      );
    } finally {
      setPending(false);
    }
  }

  function focusField(focus: ProtocolFocus | 'clinical_reason' | null) {
    const target =
      focus === 'diagnosis'
        ? diagnosisRef.current
        : focus === 'andet'
          ? andetRef.current
          : focus === 'anamnesis'
            ? anamnesisRef.current
            : focus === 'clinical_reason'
              ? clinicalReasonRef.current
              : null;
    target?.focus();
  }

  function buildRequest(rows: readonly AttachmentRow[]): Xref01Request | null {
    if (!receiver) return null;
    const receiverPair = receiverIdentifierPair(identifierParty(receiver));
    if (!medicalSpecialityCode || !patientCprDigits || !receiverPair.ok) return null;
    const reasonLines = clinicalReasonPayload(clinicalReason);
    if (!reasonLines) return null;
    const clinic = clinicQuery.data;
    const connection = connectionQuery.data;
    const sender = senderSor.data;
    const referrer = referrerForRequest(referrerQuery.data);
    if (!clinic || !connection?.ean || !sender?.identifier || !sender.identifierCode || !referrer) {
      return null;
    }
    const chosenNames = chosenRelative
      ? relatedQuery.data?.find((row) => row.id === chosenRelative.row.to_patient_id)
      : undefined;
    const relationCode = chosenRelative ? medcomRelationCode(chosenRelative.relationType) : null;
    const relativeDigits = relativeCpr ? cprDigits(relativeCpr) : null;
    const telephoneSubscribers = subscribers.slice(0, MEDCOM_MAX_TELEPHONE_SUBSCRIBERS).map((subscriber) => ({
      telephoneSubscriberIdentifier: subscriber.identifier,
      telephoneSubscriberCode: subscriber.code,
    }));
    if (!primary) return null;
    const lpr = referralLprFields({
      referringIdentifier: episodeId,
      forward: forwardLpr,
      objectId: forwardLpr ? (selectedPathway?.object_id ?? null) : null,
      illness: lprIllness,
      hospitalChoice: lprHospitalChoice,
    });
    const urls = referralReferencesForRequest(attachmentDrafts(rows), { requireChecked: true });
    const companions = referralCompanions(rows);
    if (!lpr.ok || !urls.ok) return null;

    const request: Xref01Request = {
      clinicId: patient.clinic_id,
      patientId: patient.id,
      kind: 'XREF01',
      acknowledgementCode: 'pluspositivkvitt',
      sender: {
        ean: connection.ean,
        identifier: sender.identifier,
        identifierCode: sender.identifierCode,
        organisationName: clinic.name,
        medicalSpecialityCode,
        referrer,
        ...filledAddress(clinicAddress),
        ...(clinic.phone ? { telephoneSubscriberIdentifier: clinic.phone } : {}),
      },
      receiver: receiverBody(receiver, receiverPair.identifierCode),
      patient: {
        civilRegistrationNumber: patientCprDigits,
        personSurnameName: patient.last_name,
        ...(patient.first_name ? { personGivenName: patient.first_name } : {}),
        ...(patient.job_title ? { personTitle: patient.job_title } : {}),
        ...filledAddress(address),
        ...(patient.email ? { emailIdentifier: patient.email } : {}),
        ...(telephoneSubscribers.length > 0 ? { telephoneSubscribers } : {}),
      },
      episodesOfCare: lpr.fields.episodesOfCare,
      additionalInformation: {
        priority,
        referralStatus: stay,
        transport,
        clinicalReason: reasonLines,
        ...(supplementary.length > 0 ? { supplementary: [supplementary] } : {}),
        ...(lpr.fields.hospitalChoice ? { hospitalChoice: lpr.fields.hospitalChoice } : {}),
      },
      ...(urls.references.length > 0 ? { references: urls.references } : {}),
      ...(companions.length > 0 ? { companions } : {}),
      relevantClinicalInformation: {
        anamnesis: [anamnesis],
        ...(medicine.length > 0 ? { actualMedicine: [medicine] } : {}),
        ...(cave.length > 0 ? { cave: [cave] } : {}),
        ...(examination.length > 0 ? { examination: [examination] } : {}),
      },
      referral: {
        refer: diagnosisBody(
          primary,
          referDiagnoseDescriptionCodeForRequest(
            forwardLpr,
            lpr.fields.diagnoseDescriptionCode,
            referType,
          ),
        ),
        ...(selectedAdditional.length > 0
          ? {
              additionalDiagnoses: selectedAdditional
                .slice(0, MAX_ADDITIONAL_DIAGNOSES)
                .map((row) => diagnosisBody(row.diagnosis, row.diagnoseDescriptionCode)),
            }
          : {}),
      },
    };
    if (copyReceiver) {
      const copyPair = receiverIdentifierPair(identifierParty(copyReceiver));
      if (!copyPair.ok) return null;
      request.answerCCReceiver = receiverBody(copyReceiver, copyPair.identifierCode);
    }
    if (chosenNames && relationCode && chosenNames.last_name) {
      request.relative = {
        relationCode,
        personSurnameName: chosenNames.last_name,
        ...(chosenNames.first_name ? { personGivenName: chosenNames.first_name } : {}),
        ...(relativeDigits ? { personIdentifier: relativeDigits } : {}),
      };
    }
    return request;
  }

  function onPriority(next: Priority) {
    setPriority(next);
    if (next === 'saerlige_forhold') andetRef.current?.focus();
  }

  function onForwardLpr(next: boolean) {
    setForwardLpr(next);
    if (!next || lprPathwayId) return;
    const first = pathways[0];
    if (!first) return;
    setLprPathwayId(first.id);
    setLprHospitalChoice(isLprHospitalChoiceCode(first.frit_valg) ? first.frit_valg : null);
  }

  function onLprPathway(id: string) {
    setLprPathwayId(id);
    const row = pathways.find((item) => item.id === id);
    setLprHospitalChoice(row && isLprHospitalChoiceCode(row.frit_valg) ? row.frit_valg : null);
  }

  const senderView = senderDisplay();

  const patientName = `${patient.first_name} ${patient.last_name}`.trim();

  return (
    <form onSubmit={onSubmit} className="flex flex-none flex-col">
      <ReferralFormChrome
        backTo={patientHenvisningPath(patient.id, { vis: 'sendte' })}
        backLabel={t('patients.hospital_referral.back_to_sent', 'Sendte henvisninger')}
        title={t('patients.hospital_referral.new_hospital', 'Ny sygehushenvisning')}
        subtitle={t('patients.hospital_referral.form_subtitle', 'Elektronisk henvisning for {{name}}', {
          name: patientName,
        })}
        onCancel={onCancel}
        banner={
          <ReferralBlockingNotice
            messages={alertMessages}
            relationsTo={childWithoutRelative ? patientTabPath(patient.id, 'relations') : undefined}
            relativesHref={childWithoutRelative ? '#paaroerende' : undefined}
          />
        }
        toc={[
          { id: 'modtager', label: t('patients.hospital_referral.receiver_section', 'Modtager') },
          { id: 'diagnoser', label: t('patients.hospital_referral.toc_diagnoses', 'Diagnoser') },
          { id: 'lpr', label: t('patients.hospital_referral.lpr_section', 'LPR-forløb') },
          { id: 'prioritet', label: t('patients.hospital_referral.toc_priority', 'Prioritet og ophold') },
          { id: 'undersogelse', label: t('patients.hospital_referral.toc_exam', 'Ønsket undersøgelse') },
          { id: 'kliniske', label: t('patients.hospital_referral.clinical_section', 'Kliniske oplysninger') },
          { id: 'bilag', label: t('patients.hospital_referral.attachments_section', 'Bilag') },
          { id: 'paaroerende', label: t('patients.hospital_referral.relatives_section', 'Pårørende') },
          { id: 'patient', label: t('patients.hospital_referral.patient_section', 'Patient fra stamkort') },
          { id: 'afsender', label: t('patients.hospital_referral.sender_section', 'Afsender') },
          { id: 'kvittering', label: t('patients.hospital_referral.acknowledgement_section', 'Kvittering') },
        ]}
        footerStatus={
          receiver ? (
            <>
              <Hospital className="h-4 w-4" aria-hidden />
              <span className="min-w-0 truncate">
                {t('patients.hospital_referral.footer_receiver', 'Modtager: {{name}} · EAN {{ean}}', {
                  name: receiver.organisation.name || receiver.identifier,
                  ean: receiver.organisation.ean || receiver.identifier,
                })}
              </span>
            </>
          ) : undefined
        }
        footer={
          <Button type="submit" disabled={pending} loading={pending} loadingText={t('patients.hospital_referral.submitting', 'Danner henvisning…')}>
            <Send aria-hidden />
            {t('patients.hospital_referral.submit', 'Dan henvisning')}
          </Button>
        }
      >

      <Section id="modtager" title={t('patients.hospital_referral.receiver_section', 'Modtager')}>
        <SorPicker
          label={t('patients.hospital_referral.receiver_search', 'Modtager')}
          fieldId="referral-receiver"
          selected={receiver}
          partyLabel={t('patients.hospital_referral.receiver_section', 'Modtager')}
          receives="XREF01"
          onSelect={(organisation) =>
            setReceiver({
              organisation,
              identifier: organisation.identifier ?? '',
              chosenIdentifierCode: null,
            })
          }
          onIdentifier={(identifier) =>
            setReceiver((current) => (current ? { ...current, identifier } : current))
          }
          onIdentifierCode={(chosenIdentifierCode) =>
            setReceiver((current) => (current ? { ...current, chosenIdentifierCode } : current))
          }
          onClear={() => setReceiver(null)}
        />
        {attempted && !receiver && (
          <p role="alert" className={cn('mt-2', FORM_ERROR_TYPE)}>
            {t('patients.hospital_referral.receiver_missing', 'Vælg en modtager.')}
          </p>
        )}
        <SorPicker
          label={t('patients.hospital_referral.copy_search', 'Kopimodtager af epikrise (valgfri)')}
          fieldId="referral-copy"
          selected={copyReceiver}
          partyLabel={t('patients.hospital_referral.copy_party', 'Kopimodtager')}
          receives={null}
          hide={(organisation) => !receivesEpikrise(organisation)}
          onSelect={(organisation) =>
            setCopyReceiver({
              organisation,
              identifier: organisation.identifier ?? '',
              chosenIdentifierCode: null,
            })
          }
          onIdentifier={(identifier) =>
            setCopyReceiver((current) => (current ? { ...current, identifier } : current))
          }
          onIdentifierCode={(chosenIdentifierCode) =>
            setCopyReceiver((current) => (current ? { ...current, chosenIdentifierCode } : current))
          }
          onClear={() => setCopyReceiver(null)}
          optional
        />
      </Section>

      <Section id="diagnoser" title={t('patients.hospital_referral.diagnosis_section', 'Henvisningsdiagnose')}>
        <DiagnosisField
          inputId="referral-diagnosis"
          inputRef={diagnosisRef}
          label={t('patients.hospital_referral.diagnosis_search', 'Søg diagnose')}
          selected={primary}
          onSelect={setPrimary}
          onClear={() => setPrimary(null)}
        />
        {attempted && !primary && (
          <p role="alert" className={cn('mt-2', FORM_ERROR_TYPE)}>
            {t('patients.hospital_referral.diagnosis_required', WARNING_DIAGNOSIS)}
          </p>
        )}
        <div className="mt-4">
          <Select
            id="referral-refer-type"
            label={t('patients.hospital_referral.refer_type', XREF01_REFER_TYPE_LABEL_DA)}
            value={
              forwardLpr
                ? referDiagnoseDescriptionCodeForRequest(
                    true,
                    lprIllness ?? DIAGNOSE_DESCRIPTION_CODE,
                    referType,
                  )
                : referType
            }
            disabled={forwardLpr}
            onChange={(event) =>
              setReferType(referDiagnoseDescriptionCodeOrDefault(event.target.value))
            }
          >
            {XREF01_REFER_DIAGNOSE_DESCRIPTION_CODES.map((code) => (
              <option key={code} value={code}>
                {t(`patients.hospital_referral.refer_type_${code}`, XREF01_REFER_DIAGNOSE_DESCRIPTION_LABELS_DA[code])}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <p className={FORM_LABEL_TYPE}>{t('patients.hospital_referral.additional_section', 'Tillægsdiagnoser')}</p>
          <p className={UI_MUTED}>
            {t('patients.hospital_referral.additional_hint', 'Valgfrit. Højst {{max}} tillægsdiagnoser i henvisningen.', {
              max: MAX_ADDITIONAL_DIAGNOSES,
            })}
          </p>
        {additional.map((row, index) => (
          <div key={row.key} className="mt-4">
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className={FORM_LABEL_TYPE}>
                {t('patients.hospital_referral.additional', 'Tillægsdiagnose')} {index + 1}
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setAdditional((rows) => rows.filter((item) => item.key !== row.key))}
              >
                {t('patients.hospital_referral.remove', 'Fjern')}
              </Button>
            </div>
            <Select
              id={`referral-additional-type-${row.key}`}
              label={t('patients.hospital_referral.additional_type', XREF01_ADDITIONAL_TYPE_LABEL_DA)}
              value={row.diagnoseDescriptionCode}
              onChange={(event) =>
                setAdditional((rows) =>
                  rows.map((item) =>
                    item.key === row.key
                      ? {
                          ...item,
                          diagnoseDescriptionCode: additionalDiagnoseDescriptionCodeOrDefault(
                            event.target.value,
                          ),
                        }
                      : item,
                  ),
                )
              }
            >
              {XREF01_ADDITIONAL_DIAGNOSE_DESCRIPTION_CODES.map((code) => (
                <option key={code} value={code}>
                  {t(
                    `patients.hospital_referral.additional_type_${code}`,
                    XREF01_ADDITIONAL_DIAGNOSE_DESCRIPTION_LABELS_DA[code],
                  )}
                </option>
              ))}
            </Select>
            <div className="mt-3">
            <DiagnosisField
              inputId={`referral-additional-${row.key}`}
              label={t('patients.hospital_referral.diagnosis_search', 'Søg diagnose')}
              selected={row.diagnosis}
              onSelect={(diagnosis) =>
                setAdditional((rows) =>
                  rows.map((item) => (item.key === row.key ? { ...item, diagnosis } : item)),
                )
              }
              onClear={() =>
                setAdditional((rows) =>
                  rows.map((item) => (item.key === row.key ? { ...item, diagnosis: null } : item)),
                )
              }
            />
            </div>
          </div>
        ))}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={!canAddAdditionalDiagnosis(additional.length)}
            aria-describedby={
              canAddAdditionalDiagnosis(additional.length) ? undefined : 'referral-additional-limit'
            }
            onClick={() =>
              setAdditional((rows) =>
                canAddAdditionalDiagnosis(rows.length)
                  ? [
                      ...rows,
                      {
                        key: crypto.randomUUID(),
                        diagnosis: null,
                        diagnoseDescriptionCode: XREF01_ADDITIONAL_DIAGNOSE_DEFAULT,
                      },
                    ]
                  : rows,
              )
            }
          >
            <Plus aria-hidden />
            {t('patients.hospital_referral.add_additional', 'Tilføj tillægsdiagnose')}
          </Button>
          {!canAddAdditionalDiagnosis(additional.length) && (
            <p id="referral-additional-limit" className={UI_MUTED}>
              {t(
                'patients.hospital_referral.additional_limit_reached',
                'Der er tilføjet {{max}} tillægsdiagnoser, som er det højeste antal i en henvisning. Fjern en for at tilføje en anden.',
                { max: MAX_ADDITIONAL_DIAGNOSES },
              )}
            </p>
          )}
        </div>
        </div>
      </Section>

      <Section id="lpr" title={t('patients.hospital_referral.lpr_section', 'LPR-forløb')}>
        <HospitalReferralLpr
          pathways={pathways}
          pathwaysLoading={pathwaysQuery.isLoading}
          pathwaysError={pathwaysQuery.isError}
          fritvalg={fritvalgCodes}
          fritvalgLoading={sksQuery.isLoading}
          forward={forwardLpr}
          pathwayId={lprPathwayId}
          illness={lprIllness}
          hospitalChoice={lprHospitalChoice}
          attempted={attempted}
          onForward={onForwardLpr}
          onPathway={onLprPathway}
          onIllness={(code) => {
            if (isLprIllness(code)) setLprIllness(code);
          }}
          onHospitalChoice={setLprHospitalChoice}
        />
      </Section>

      <Section id="prioritet" title={t('patients.hospital_referral.priority_section', 'Prioritet, ophold, befordring')}>
        <div className="hv-pri">
          <RadioGroup
            name="referral-priority"
            legend={t('patients.hospital_referral.priority', 'Prioritet')}
            value={priority}
            onChange={onPriority}
            options={[
              { value: 'elektiv', label: t('patients.hospital_referral.priority_elektiv', 'Elektiv') },
              { value: 'akut', label: t('patients.hospital_referral.priority_akut', 'Akut') },
              {
                value: 'saerlige_forhold',
                label: t('patients.hospital_referral.priority_special', 'Se særlige forhold'),
              },
            ]}
          />
          <RadioGroup
            name="referral-stay"
            legend={t('patients.hospital_referral.stay', 'Ophold')}
            value={stay}
            onChange={setStay}
            options={[
              { value: 'ambulant', label: t('patients.hospital_referral.stay_ambulant', 'Ambulant') },
              {
                value: 'indlaeggelse',
                label: t('patients.hospital_referral.stay_admission', 'Indlæggelse'),
              },
            ]}
          />
          <RadioGroup
            name="referral-transport"
            legend={t('patients.hospital_referral.transport', 'Befordring')}
            value={transport}
            onChange={setTransport}
            options={[
              { value: 'ingen', label: t('patients.hospital_referral.transport_none', 'Ingen') },
              { value: 'liggende', label: t('patients.hospital_referral.transport_lying', 'Liggende') },
              { value: 'siddende', label: t('patients.hospital_referral.transport_sitting', 'Siddende') },
            ]}
          />
        </div>
      </Section>

      <Section id="undersogelse" title={t('patients.hospital_referral.clinical_reason', 'Ønsket undersøgelse/behandling/problemstilling')}>
        <TextArea
          id="referral-clinical-reason"
          fieldLabel={t(
            'patients.hospital_referral.clinical_reason',
            'Ønsket undersøgelse/behandling/problemstilling',
          )}
          textRef={clinicalReasonRef}
          value={clinicalReason}
          onChange={setClinicalReason}
          max={TEXT_FIELD_MAX}
        />
        {attempted && !hasClinicalReason(clinicalReason) && (
          <p role="alert" className={cn('mt-2', FORM_ERROR_TYPE)}>
            {t('patients.hospital_referral.clinical_reason_required', MISSING_CLINICAL_REASON_MESSAGE)}
          </p>
        )}
      </Section>

      <Section
        title={t('patients.hospital_referral.andet', 'Andet')}
        hint={t('patients.hospital_referral.andet_hint', 'Praktiske forhold, tolk, afhentningsadresse.')}
      >
        <TextArea
          id="referral-andet"
          fieldLabel={t('patients.hospital_referral.andet', 'Andet')}
          textRef={andetRef}
          value={supplementary}
          onChange={setSupplementary}
          max={TEXT_FIELD_MAX}
        />
        {attempted && priority === 'saerlige_forhold' && supplementary.trim().length === 0 && (
          <p role="alert" className={cn('mt-2', FORM_ERROR_TYPE)}>
            {t('patients.hospital_referral.andet_required', WARNING_ANDET)}
          </p>
        )}
      </Section>

      <Section
        id="kliniske"
        title={t('patients.hospital_referral.clinical_section', 'Kliniske oplysninger')}
        hint={t(
          'patients.hospital_referral.clinical_total_hint',
          'Anamnese, medicin, CAVE og øvrige undersøgelser: {{count}} / {{max}} tegn i alt.',
          {
            count: clinicalLength,
            max: CLINICAL_INFO_MAX_TOTAL.toLocaleString(uiDateLocale()),
          },
        )}
      >
        {clinicalLength > CLINICAL_INFO_MAX_TOTAL && (
          <p role="alert" className={FORM_ERROR_TYPE}>
            {t(
              'patients.hospital_referral.clinical_over',
              'Anamnese, medicin, CAVE og øvrige undersøgelser er tilsammen på {{count}} tegn. Grænsen er {{max}}.',
              {
                count: clinicalLength,
                max: CLINICAL_INFO_MAX_TOTAL.toLocaleString(uiDateLocale()),
              },
            )}
          </p>
        )}
        <div className="hv-g2">
          <LabeledText
            id="referral-anamnesis"
            textRef={anamnesisRef}
            label={t('patients.hospital_referral.anamnesis', 'Anamnese')}
            value={anamnesis}
            onChange={setAnamnesis}
          />
          {attempted && anamnesis.trim().length === 0 && (
            <p role="alert" className={FORM_ERROR_TYPE}>
              {t('patients.hospital_referral.anamnesis_required', WARNING_ANAMNESIS)}
            </p>
          )}
          <LabeledText
            id="referral-medicine"
            label={t('patients.hospital_referral.medicine', 'Medicin')}
            value={medicine}
            onChange={setMedicine}
          />
          <LabeledText
            id="referral-cave"
            label={t('patients.hospital_referral.cave', 'CAVE')}
            value={cave}
            onChange={setCave}
          />
          <LabeledText
            id="referral-examination"
            label={t('patients.hospital_referral.examination', 'Øvrige undersøgelser')}
            value={examination}
            onChange={setExamination}
          />
        </div>
      </Section>

      <Section
        id="bilag"
        title={t('patients.hospital_referral.attachments_section', 'Bilag')}
        hint={t(
          'patients.hospital_referral.attachments_hint',
          'Et bilag er en beskrivelse og enten en webadresse eller en fil. Henvisningen medtager adressen eller en reference til filen. Selve filen ligger i et separat bilag.',
        )}
      >
        <HospitalReferralAttachments
          rows={attachments}
          attempted={attempted}
          onChangeUrl={(key, patch) => {
            setAttachments((rows) =>
              rows.map((row) => (row.key === key && row.kind === 'url' ? { ...row, ...patch } : row)),
            );
          }}
          onChangeFileDescription={(key, description) => {
            setAttachments((rows) =>
              rows.map((row) => (row.key === key && row.kind === 'file' ? { ...row, description } : row)),
            );
          }}
          onChooseFile={(key, file) => {
            setAttachments((rows) => applyChosenFile(rows, key, file));
          }}
          onChangeZipObjectType={(key, zipObjectType) => {
            // A new content type means a new XBIN02: the file is uploaded again on submit.
            setAttachments((rows) =>
              rows.map((row) =>
                row.key === key && row.kind === 'file'
                  ? {
                      ...row,
                      zipObjectType,
                      exchangeId: null,
                      objectIdentifier: '',
                      objectCode: '',
                      objectExtensionCode: '',
                      originalObjectSize: null,
                      error: '',
                    }
                  : row,
              ),
            );
          }}
          onAddUrl={() => {
            setAttachments((rows) =>
              rows.length >= MAX_REFERENCES
                ? rows
                : [...rows, { key: newEpisodeIdentifier(), kind: 'url', description: '', url: '' }],
            );
          }}
          onAddFile={() => {
            setAttachments((rows) =>
              rows.length >= MAX_REFERENCES ? rows : [...rows, emptyFileRow(newEpisodeIdentifier())],
            );
          }}
          onRemove={(key) => {
            setAttachments((rows) => rows.filter((row) => row.key !== key));
          }}
        />
      </Section>

      <Section
        id="paaroerende"
        title={t('patients.hospital_referral.relatives_section', 'Pårørende')}
        hint={
          relativeUnder15
            ? t(
                'patients.hospital_referral.relatives_choose_under15',
                WARNING_UNDER15_CHOOSE_RELATIVE,
              )
            : t(
                'patients.hospital_referral.relatives_choose',
                'Vælg den pårørende, der medtages i henvisningen. Der kan højst medtages én. Som udgangspunkt vælges moren, ellers faren, ellers den første øvrige personrelation.',
              )
        }
      >
        <ReferralRelativesChoice
          idPrefix="referral"
          relations={personRelations}
          selectedId={chosenRelative?.row.id ?? null}
          under15={relativeUnder15}
          childWithoutRelative={childWithoutRelative}
          emptyLabel={emptyLabel}
          onChoose={setRelativeChoice}
        />
      </Section>

      <Section
        id="patient"
        title={t('patients.hospital_referral.patient_section', 'Patient fra stamkort')}
        hint={t('patients.hospital_referral.stamkort_hint', 'Oplysningerne hentes fra stamkortet og rettes under Rediger patient.')}
      >
        <Grid>
          <ReadOnly
            label={t('patients.hospital_referral.cpr', 'CPR')}
            value={cprDisplay(patient.has_cpr, canRevealCpr, cprFailed, revealedCpr, emptyLabel, t)}
          />
          <ReadOnly label={t('patients.hospital_referral.surname', 'Efternavn')} value={patient.last_name || emptyLabel} />
          <ReadOnly label={t('patients.hospital_referral.given_name', 'Fornavn')} value={patient.first_name || emptyLabel} />
          <ReadOnly label={t('patients.hospital_referral.street', 'Adresse')} value={address.street || emptyLabel} />
          <ReadOnly label={t('patients.hospital_referral.post_code', 'Postnr')} value={address.postCode || emptyLabel} />
          <ReadOnly label={t('patients.hospital_referral.city', 'By')} value={address.district || emptyLabel} />
          <ReadOnly label={t('patients.hospital_referral.job_title', 'Stilling')} value={patient.job_title || emptyLabel} />
          <ReadOnly label={t('patients.hospital_referral.email', 'E-mail')} value={patient.email || emptyLabel} />
          {PHONE_ROWS.map((row) => (
            <ReadOnly
              key={row.code}
              label={t(`patients.hospital_referral.phone_${row.code}`, row.label)}
              value={
                subscribers
                  .filter((subscriber) => subscriber.code === row.code)
                  .map((subscriber) => subscriber.identifier)
                  .join(', ') || emptyLabel
              }
            />
          ))}
        </Grid>
      </Section>

      <Section id="afsender" title={t('patients.hospital_referral.sender_section', 'Afsender')}>
        <Grid>
          <ReadOnly label={t('patients.hospital_referral.ean', 'EAN')} value={senderView.ean} />
          <ReadOnly
            label={t('patients.hospital_referral.location_number', 'Lokationsnummer')}
            value={senderView.location}
          />
          <ReadOnly
            label={t('patients.hospital_referral.identifier_code', 'Identifikatorkode')}
            value={senderView.identifierCode}
          />
          <ReadOnly
            label={t('patients.hospital_referral.organisation', 'Organisation')}
            value={senderView.organisation}
          />
          <ReadOnly label={t('patients.hospital_referral.street', 'Adresse')} value={senderView.street} />
          <ReadOnly label={t('patients.hospital_referral.post_code', 'Postnr')} value={senderView.postCode} />
          <ReadOnly label={t('patients.hospital_referral.city', 'By')} value={senderView.city} />
          <ReadOnly label={t('patients.hospital_referral.phone', 'Telefon')} value={senderView.phone} />
          {assessment.warnings.length > 0 ? (
            <div
              role="status"
              className="rounded-md border border-ds-amber-line bg-ds-amber-tint p-3 sm:col-span-2"
            >
              {assessment.warnings.map((message, index) => (
                <p
                  key={`${index}-${message}`}
                  className={cn(TYPE.body, 'text-ds-amber-ink ')}
                >
                  {message}
                </p>
              ))}
            </div>
          ) : null}
          <ReadOnly
            label={t('patients.hospital_referral.speciality', 'Speciale')}
            value={
              medicalSpecialityCode
                ? medicalSpecialityLabel(medicalSpecialityCode)
                : t('patients.hospital_referral.speciality_missing', 'Ikke konfigureret')
            }
          />
          <ReadOnly
            label={t('patients.hospital_referral.referrer_name', 'Henvisers navn')}
            value={senderView.referrer}
          />
          <ReadOnly
            label={t('patients.hospital_referral.referrer_title', 'Henvisers titel')}
            value={senderView.referrerTitle}
          />
        </Grid>
        {medicalSpecialityCode === null && (
          <p role="alert" className={cn('mt-3', FORM_ERROR_TYPE)}>
            {t(
              'patients.hospital_referral.speciality_block',
              'Klinikkens specialekode er ikke sat op. Henvisningen kan ikke dannes, før specialekoden er konfigureret.',
            )}
          </p>
        )}
      </Section>

      <Section id="kvittering" title={t('patients.hospital_referral.acknowledgement_section', 'Kvittering')}>
        <p className={TYPE.body}>
          {t('patients.hospital_referral.acknowledgement', 'Der bedes om positiv kvittering.')}
        </p>
      </Section>

      <ReferralSubmitError message={submitError} />
      </ReferralFormChrome>

      {preview ? (
        <ReferralBuiltPreview
          xml={preview.xml}
          checks={preview.checks}
          ok={preview.ok}
          xmlFileName={`XREF01_${episodeId}.xml`}
        />
      ) : null}
    </form>
  );

  function senderDisplay() {
    const loading = t('patients.hospital_referral.loading', 'Henter…');
    const clinic = clinicQuery.data;
    const connection = connectionQuery.data;
    if (clinicQuery.isLoading || connectionQuery.isLoading) {
      return blankSender(loading);
    }
    const location = !connection?.sor_id
      ? t('patients.hospital_referral.sender_sor_missing', 'Klinikken har ikke et SOR-id.')
      : senderSor.isLoading
        ? loading
        : senderSor.data?.identifier ??
          t(
            'patients.hospital_referral.sender_identifier_missing',
            'Lokationsnummeret kan ikke udledes af klinikkens SOR-enhed.',
          );
    return {
      ean: connection?.ean || emptyLabel,
      location,
      identifierCode: senderSor.data?.identifierCode || emptyLabel,
      organisation: clinic?.name || emptyLabel,
      street: clinicAddress.street || emptyLabel,
      postCode: clinicAddress.postCode || emptyLabel,
      city: clinicAddress.district || emptyLabel,
      phone: clinic?.phone || emptyLabel,
      referrer: referrerQuery.isLoading
        ? loading
        : referrerQuery.data?.name || emptyLabel,
      referrerTitle: referrerQuery.isLoading
        ? loading
        : referrerQuery.data?.title || emptyLabel,
    };
  }
}

function isChildAge(cpr: string, today: Date): boolean {
  return under15BlocksReferral(cpr, [], today);
}

function blankSender(value: string) {
  return {
    ean: value,
    location: value,
    identifierCode: value,
    organisation: value,
    street: value,
    postCode: value,
    city: value,
    phone: value,
    referrer: value,
    referrerTitle: value,
  };
}

function cprDisplay(
  hasCpr: boolean,
  canReveal: boolean,
  failed: boolean,
  revealed: string | null,
  emptyLabel: string,
  t: (key: string, fallback: string) => string,
): string {
  if (!hasCpr) return emptyLabel;
  if (!canReveal) return t('patients.hospital_referral.cpr_unavailable', 'CPR-nummeret kan ikke vises. Henvisningen kan ikke dannes.');
  if (failed) return t('patients.hospital_referral.cpr_failed', 'CPR-nummeret kunne ikke læses. Henvisningen kan ikke dannes.');
  if (!revealed) return t('patients.hospital_referral.loading', 'Henter…');
  return formatCprDisplay(revealed);
}

function senderContactWarningText(
  t: (key: string, fallback: string) => string,
  gaps: readonly SenderContactGap[],
): string {
  const missingPostcode = gaps.includes('postcode');
  const missingPhone = gaps.includes('phone');
  if (missingPostcode && missingPhone) {
    return t(
      'patients.hospital_referral.sender_contact_missing_both',
      'Klinikkens postnummer og telefonnummer mangler. MedComs egne eksempler har altid begge på afsenderen. Udfyld dem under klinikkens indstillinger.',
    );
  }
  if (missingPostcode) {
    return t(
      'patients.hospital_referral.sender_postcode_missing',
      'Klinikkens postnummer mangler. MedComs egne eksempler har altid et postnummer på afsenderen. Udfyld det under klinikkens indstillinger.',
    );
  }
  return t(
    'patients.hospital_referral.sender_phone_missing',
    'Klinikkens telefonnummer mangler. MedComs egne eksempler har altid et telefonnummer på afsenderen. Udfyld det under klinikkens indstillinger.',
  );
}

function filledAddress(address: { street: string; postCode: string; district: string }): {
  streetName?: string;
  postCodeIdentifier?: string;
  districtName?: string;
} {
  return {
    ...(address.street ? { streetName: address.street } : {}),
    ...(address.postCode ? { postCodeIdentifier: address.postCode } : {}),
    ...(address.district ? { districtName: address.district } : {}),
  };
}

function identifierParty(picked: PickedReceiver): ReceiverIdentifierParty {
  return {
    registerCode: picked.organisation.identifierCode,
    chosenCode: picked.chosenIdentifierCode,
    identifier: picked.identifier,
  };
}

function receiverBody(picked: PickedReceiver, identifierCode: Xref01IdentifierCode): Xref01Request['receiver'] {
  return {
    ean: picked.organisation.ean,
    identifier: picked.identifier,
    identifierCode,
    ...(picked.organisation.name ? { organisationName: picked.organisation.name } : {}),
    ...filledAddress({
      street: picked.organisation.address.street ?? '',
      postCode: picked.organisation.address.postCode ?? '',
      district: picked.organisation.address.district ?? '',
    }),
  };
}

function pushAddressLengths(
  problems: string[],
  tooLong: (field: string, length: number, max: number) => string,
  address: { street: string; postCode: string; district: string },
) {
  if (address.street.length > PARTY_MAX.street) {
    problems.push(tooLong('Adresse', address.street.length, PARTY_MAX.street));
  }
  if (address.postCode.length > PARTY_MAX.postCode) {
    problems.push(tooLong('Postnr', address.postCode.length, PARTY_MAX.postCode));
  }
  if (address.district.length > PARTY_MAX.district) {
    problems.push(tooLong('By', address.district.length, PARTY_MAX.district));
  }
}

function identifierGapMessages(
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
  label: string,
  missing: IdentifierPairGap,
): string[] {
  const codeMessage = t(
    'patients.hospital_referral.identifier_code_missing',
    '{{label}}: Identifikatorkoden mangler.',
    { label },
  );
  const identifierMessage = t(
    'patients.hospital_referral.identifier_missing',
    '{{label}}: Identifikatoren mangler.',
    { label },
  );
  switch (missing) {
    case 'both':
      return [codeMessage, identifierMessage];
    case 'code':
      return [codeMessage];
    case 'identifier':
      return [identifierMessage];
    default: {
      const unexpected: never = missing;
      return [unexpected];
    }
  }
}

function partyProblems(
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
  tooLong: (field: string, length: number, max: number) => string,
  label: string,
  picked: PickedReceiver,
): string[] {
  const problems: string[] = [];
  const org = picked.organisation;
  const pair = receiverIdentifierPair(identifierParty(picked));
  if (!pair.ok) problems.push(...identifierGapMessages(t, label, pair.missing));
  if (picked.identifier.length > PARTY_MAX.identifier) {
    problems.push(tooLong(`${label} identifikator`, picked.identifier.length, PARTY_MAX.identifier));
  }
  if (org.ean.length > PARTY_MAX.ean) problems.push(tooLong(`${label} EAN`, org.ean.length, PARTY_MAX.ean));
  if (org.name.length > PARTY_MAX.organisationName) {
    problems.push(tooLong(`${label} organisation`, org.name.length, PARTY_MAX.organisationName));
  }
  const street = org.address.street ?? '';
  const postCode = org.address.postCode ?? '';
  const district = org.address.district ?? '';
  if (street.length > PARTY_MAX.street) problems.push(tooLong(`${label} adresse`, street.length, PARTY_MAX.street));
  if (postCode.length > PARTY_MAX.postCode) problems.push(tooLong(`${label} postnr`, postCode.length, PARTY_MAX.postCode));
  if (district.length > PARTY_MAX.district) problems.push(tooLong(`${label} by`, district.length, PARTY_MAX.district));
  for (const value of [org.ean, org.name, street, postCode, district, picked.identifier]) {
    if (firstNonLatin1(value) !== null) {
      problems.push(
        t(
          'patients.hospital_referral.latin1',
          '{{field}} indeholder et tegn, der ikke kan skrives i ISO-8859-1.',
          { field: label },
        ),
      );
      break;
    }
  }
  return problems;
}

type MedbinFileResult = {
  ok: boolean;
  parentReference: string | null;
  exchangeId: string;
  objectCode: string;
  objectExtensionCode: string;
  originalObjectSize: number;
  checks: { id: string; pass: boolean; detail: string }[];
};

function attachmentDrafts(rows: readonly AttachmentRow[]): AttachmentDraft[] {
  return rows.map((row) =>
    row.kind === 'url'
      ? { kind: 'url', description: row.description, url: row.url }
      : {
          kind: 'file',
          description: row.description,
          fileName: row.fileName,
          byteLength: row.byteLength,
          objectIdentifier: row.objectIdentifier,
          objectCode: row.objectCode,
          objectExtensionCode: row.objectExtensionCode,
          originalObjectSize: row.originalObjectSize,
        },
  );
}

function referralCompanions(rows: readonly AttachmentRow[]): { exchangeId: string; fileName: string }[] {
  const companions: { exchangeId: string; fileName: string }[] = [];
  for (const row of rows) {
    if (row.kind === 'file' && row.exchangeId && row.fileName.trim().length > 0) {
      companions.push({ exchangeId: row.exchangeId, fileName: row.fileName.trim() });
    }
  }
  return companions;
}

function emptyFileRow(key: string): FileAttachmentRow {
  return {
    key,
    kind: 'file',
    description: '',
    fileName: '',
    byteLength: 0,
    file: null,
    objectIdentifier: '',
    objectCode: '',
    objectExtensionCode: '',
    originalObjectSize: null,
    exchangeId: null,
    error: '',
  };
}

function applyChosenFile(
  rows: readonly AttachmentRow[],
  key: string,
  file: File,
): AttachmentRow[] {
  const others = rows.filter(
    (row): row is FileAttachmentRow => row.kind === 'file' && row.key !== key && row.fileName.trim().length > 0,
  );
  const limit = checkReferralFile({
    fileName: file.name,
    byteLength: file.size,
    existingFileCount: others.length,
    existingTotalBytes: others.reduce((sum, row) => sum + row.byteLength, 0),
  });
  return rows.map((row) => {
    if (row.key !== key || row.kind !== 'file') return row;
    if (!limit.ok) {
      return {
        ...row,
        file: null,
        fileName: file.name,
        byteLength: file.size,
        exchangeId: null,
        objectIdentifier: '',
        objectCode: '',
        objectExtensionCode: '',
        originalObjectSize: null,
        error: '',
      };
    }
    return {
      ...row,
      file,
      fileName: file.name,
      byteLength: file.size,
      exchangeId: null,
      objectIdentifier: '',
      objectCode: '',
      objectExtensionCode: '',
      originalObjectSize: null,
      error: '',
    };
  });
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let index = 0; index < bytes.length; index += chunk) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunk));
  }
  return btoa(binary);
}

function gatewayFallback(t: (key: string, fallback: string) => string): string {
  return t('patients.hospital_referral.attachment_file_refused', 'Filen blev afvist.');
}

function gatewayFailureText(err: unknown, fallback: string): string {
  if (!(err instanceof ApiCallError)) return fallback;
  const message = err.message.trim();
  if (!message) return fallback;
  const code = detailCode(err.details);
  if (code && !message.includes(code)) return `${message} (${code})`;
  return message;
}

function detailCode(details: unknown): string | null {
  if (!details || typeof details !== 'object' || !('code' in details)) return null;
  const code = details.code;
  return typeof code === 'string' && code.length > 0 ? code : null;
}
